import { z } from "zod";
import { Id, NonEmpty } from "../base";
import { TimestampSchema } from "../epoch";
import { XAccountSchema } from "./x";
import { ConnectedWalletSchema, WalletChallengesByAddressSchema, } from "./wallet";
import { IdentityMetadataSchema } from "./identity";
import { userTypeValues, userStatusValues, whitelistedImageContentTypeValues, } from "../constant_values";
//#region Schema
export const UserStatusSchema = z.enum(userStatusValues);
export const GetUserPublicProfileParamsSchema = z.object({
    userId: Id,
});
const _socialHandle = NonEmpty.max(200);
const SocialAccountSchema = z.object({
    url: z.string().url().max(2048),
    username: _socialHandle,
});
const SocialsSchema = z
    .object({
    twitter: SocialAccountSchema.optional(),
    discord: SocialAccountSchema.optional(),
})
    .partial();
const PrivacySchema = z
    .object({
    showWallets: z.boolean().optional(),
    showActivity: z.boolean().optional(),
})
    .partial();
export const userTypeSchema = z.enum(userTypeValues);
export const AvatarSchema = z.object({
    url: z.string().nullable(),
    storagePath: z.string().nullable().optional(),
    createdAt: TimestampSchema.optional(),
    updatedAt: TimestampSchema.optional(),
});
const UserEditableFieldsSchema = z.object({
    displayName: z.string().min(2).max(120),
    avatar: AvatarSchema.optional(),
    bio: z.string().max(500).optional(),
    location: z.string().max(120).optional(),
    privacy: PrivacySchema.optional(),
    userType: userTypeSchema.optional(),
    xAccount: XAccountSchema.nullable().optional(),
});
export const UpdateProfileSchema = UserEditableFieldsSchema.partial()
    .extend({
    id: Id,
})
    // Schema-level defense: only user-editable fields are exposed here; server-owned
    // properties (status, rank, balances, verification flags, etc.) are intentionally
    // absent and guarded again in service layer assertions.
    .refine(({ ...rest }) => Object.keys(rest).length > 0, {
    message: "At least one field must be provided",
    path: [],
});
const ServerOwnedUserProfileFieldsSchema = z.object({
    status: UserStatusSchema,
    blockBalance: z.number(),
    socials: SocialsSchema.optional(),
    verified: z.boolean().optional(),
    authWalletAddress: z.string().regex(/^0x[a-fA-F0-9]{40}$/).optional(),
    identity: IdentityMetadataSchema.optional(),
    emailVerifiedAt: TimestampSchema.optional(),
    connectedWallets: z.array(ConnectedWalletSchema).optional(),
    rank: z.number().optional(),
    isAmbassador: z.boolean().optional(),
    referrals: z.number().optional(),
    referralCode: z.string().optional(),
    referredByUserId: Id.optional(),
    referredAt: TimestampSchema.optional(),
    joinedDate: TimestampSchema,
    walletChallenges: WalletChallengesByAddressSchema.optional(),
    initialUserType: userTypeSchema.optional(),
    last_activity: TimestampSchema.optional(),
    lastAuthActivityAt: TimestampSchema.optional(),
    lastAuthEvent: z
        .enum(["authenticated_request", "sign_in", "token_refresh", "sign_out"])
        .optional(),
});
export const UserProfileRecordSchema = UserEditableFieldsSchema.extend({
    ...ServerOwnedUserProfileFieldsSchema.shape,
    id: Id,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.optional(),
});
export const AdminUpdateUserProfileSchema = ServerOwnedUserProfileFieldsSchema.extend({
    createdAt: TimestampSchema.optional(),
    updatedAt: TimestampSchema.optional(),
    ...UserEditableFieldsSchema.shape,
})
    .partial()
    .extend({
    id: Id,
    updatedAt: TimestampSchema,
});
export const AvatarUploadSchema = z.object({
    contentType: z
        .string()
        .refine((value) => whitelistedImageContentTypeValues.includes(value), {
        message: "Unsupported content type",
    }),
});
export const AvatarCompleteSchema = z.object({
    objectPath: NonEmpty,
});
//#endregion Types
//#region Interfaces
export const UserBadges = [
    "Normie",
    "Builder",
    "Architect",
    "OG",
    "Ambassador",
];
//#endregion Interfaces
